/**
 * ThermostatChart_V2 - Per-Device Thermostat Chart
 *
 * Displays 15-minute interval data for individual thermostat devices with:
 * - Consumption bars (color-coded for reserve heating)
 * - Temperature lines (target + measured)
 * - Dual Y-axis (consumption left, temperature right)
 * - InfoBox with device summary
 */

import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { InfoBox } from '../../Components';
import { formatKWh, formatCelcius } from '../ChartUtils';
import {
  extractTimeseries,
  extractDeviceData,
  aggregateDeviceDataToHourly,
  getHourlyTicks,
  formatHourLabel,
  getResponsiveBarSize,
  isMobileLayout
} from './heatingChartUtils';
import { t } from '../../i18n';

const ThermostatChart_V2 = ({ data, thermostatId, metadata, isMobile: isMobileProp = null, showDetails = false }) => {
  console.log('ThermostatChart_V2: Rendering device', thermostatId);

  const chartRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Extract timeseries and device data
  const timeseries = useMemo(() => extractTimeseries(data), [data]);
  const deviceData = useMemo(() =>
    extractDeviceData(timeseries, thermostatId),
    [timeseries, thermostatId]
  );

  // Device details from metadata
  const deviceDetails = metadata.thermostat_details?.[thermostatId];

  // Determine if mobile layout (use prop if provided, otherwise detect)
  const isMobile = useMemo(() => {
    return isMobileProp !== null ? isMobileProp : isMobileLayout(containerWidth);
  }, [isMobileProp, containerWidth]);

  // Process chart data (15-min or hourly based on mobile + toggle)
  const chartData = useMemo(() => {
    const shouldAggregate = isMobile && !showDetails;
    const source = shouldAggregate ? aggregateDeviceDataToHourly(deviceData) : deviceData;

    return source.map(snapshot => {
      // For thermostats, override should be a temperature value (>1), not just binary 1/0
      // Binary values (1 or 0) are for consumption devices, not thermostats
      const hasOverride = snapshot.override && snapshot.override > 1;
      return {
        timestamp: snapshot.timestamp,
        consumption: snapshot.consumption || 0,
        currentTemp: snapshot.current_temp || snapshot.measured_temp, // Use current_temp (primary) or measured_temp (fallback)
        targetTemp: hasOverride ? null : snapshot.target_temp, // Hide targetTemp when override is active
        override: hasOverride ? snapshot.override : null, // Show override only when active (and valid temp value)
        reserve_heating: snapshot.reserve_heating || false
      };
    });
  }, [deviceData, isMobile, showDetails]);

  // Get hourly ticks
  const hourlyTicks = useMemo(() => {
    if (isMobile && !showDetails) {
      // Show fewer ticks on mobile hourly view
      return chartData
        .filter((_, index) => index % 2 === 0) // Every 2 hours
        .map(d => d.timestamp);
    }
    return getHourlyTicks(chartData);
  }, [chartData, isMobile, showDetails]);

  // Bar size based on container width
  const barSize = useMemo(() => {
    return getResponsiveBarSize(containerWidth);
  }, [containerWidth]);

  // Update container width
  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };

    const timer = setTimeout(updateWidth, 100);
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, []);

  // Early return after all hooks
  if (!deviceDetails) {
    console.warn('No device details found for', thermostatId);
    return null;
  }

  // InfoBox data
  const thermostatInfo = [
    { label: t('heatingChart.totalConsumption'), value: formatKWh(deviceDetails.total_consumption) },
   // { label: t('heatingChart.totalCost'), value: `€${(deviceDetails.total_cost || 0).toFixed(2)}` },
    { label: t('heatingChart.avgTemp'), value: formatCelcius(deviceDetails.temp_avg) },
    { label: t('heatingChart.minTemp'), value: formatCelcius(deviceDetails.min_temp) },
    { label: t('heatingChart.maxTemp'), value: formatCelcius(deviceDetails.max_temp) }
  ];

  // Device colors
  const deviceColors = {
    normal: '#8884d8',
    reserve: '#228b22'
  };

  // Custom bar shape (darker if reserve heating)
  const CustomBarShape = (props) => {
    const { x, y, width, height, payload } = props;
    const fill = payload.reserve_heating ? deviceColors.reserve : deviceColors.normal;
    return <rect x={x} y={y} width={width} height={height} fill={fill} />;
  };

  // Check if any reserve heating or override occurred
  const hasReserveHeating = chartData.some(d => d.reserve_heating);
  const hasOverride = chartData.some(d => d.override !== null && d.override > 1);

  // Custom legend
  const customLegend = [
    { value: t('heatingChart.consumption'), type: 'square', color: deviceColors.normal },
    ...(hasReserveHeating ? [
      { value: t('heatingChart.chargingHeatRetainer'), type: 'square', color: deviceColors.reserve }
    ] : []),
    { value: t('heatingChart.targetTemp'), type: 'line', color: '#8884d8' },
    { value: t('heatingChart.measuredTemp'), type: 'line', color: '#82ca9d' },
    ...(hasOverride ? [
      { value: t('heatingChart.override'), type: 'line', color: '#FFA500' }
    ] : [])
  ];

  // Validation
  if (chartData.length === 0) {
    console.warn('ThermostatChart_V2: No chart data for device', thermostatId);
    return (
      <div>
        <InfoBox title={deviceDetails.name} dataPoints={thermostatInfo} />
        <p style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
          No data available for this device
        </p>
      </div>
    );
  }

  return (
    <div>
      <InfoBox title={deviceDetails.name} dataPoints={thermostatInfo} />

      <div ref={chartRef}>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart
            data={chartData}
            margin={{ top: 20, right: 30, bottom: 20, left: 20 }}
          >
            <CartesianGrid stroke="#f5f5f5" />

            <XAxis
              dataKey="timestamp"
              tickFormatter={formatHourLabel}
              ticks={hourlyTicks}
              interval={0}
              tick={{ fontSize: 12 }}
            />

            <YAxis
              yAxisId="left"
              orientation="left"
              label={{ value: t('heatingChart.consumption') + ' (kWh)', angle: -90, position: 'insideLeft' }}
              domain={['auto', 'auto']}
              stroke="#413ea0"
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              label={{ value: t('heatingChart.temperature') + ' (°C)', angle: 90, position: 'insideRight' }}
              domain={[10, 'auto']}
              stroke="#8884d8"
            />

            <Tooltip
              formatter={(value, name) => {
                if (name.includes('Temp') || name.includes('temp')) {
                  return [formatCelcius(value), name];
                }
                if (name.includes('Consumption') || name.includes('consumption')) {
                  return [formatKWh(value), name];
                }
                return [value, name];
              }}
            />

            <Legend payload={customLegend} />

            {/* Temperature Lines */}
            <Line
              yAxisId="right"
              type="stepAfter"
              dataKey="targetTemp"
              name={t('heatingChart.targetTemp')}
              stroke="#8884d8"
              strokeWidth={2}
              dot={false}
              connectNulls={false}
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="currentTemp"
              name={t('heatingChart.measuredTemp')}
              stroke="#82ca9d"
              strokeWidth={2}
              dot={false}
            />

            {/* Override Line - Orange line when override is active */}
            <Line
              yAxisId="right"
              type="stepAfter"
              dataKey="override"
              name={t('heatingChart.override')}
              stroke="#FFA500"
              strokeWidth={2}
              dot={false}
              connectNulls={false}
            />

            {/* Consumption Bars */}
            <Bar
              yAxisId="left"
              dataKey="consumption"
              name={t('heatingChart.consumption')}
              shape={<CustomBarShape />}
              barSize={barSize}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p></p>
    </div>
  );
};

export default ThermostatChart_V2;
